import {FIELD_SELECTED_ORDER, FIELD_ADDITIONAL_ITEM_QTY, PaymentPlans, PaymentMethod} from '../common/constants.js';
import {objectUtils, MAX_DATE_IN_SERVER_FORMAT} from './utils.js';
import {CreditTypes} from './constants';
import {translate} from '../i18n/translate.js';

/**
 * Returns array of items which has quantity > 0, aka "ordered"
 * @param items
 * @param sort - optional param for sorting ordered items as they should appear at the order summary list
 * @returns {Array}
 */
export function getOrderedItems (items, sort = true) {
	let filteredItems = [];
	for (let key in items) {
		if (items.hasOwnProperty(key) && items[key].quantity) {
			filteredItems.push(Object.assign({}, items[key]));
		}
	}
	if (sort && filteredItems.length) {
		filteredItems.sort((i1, i2)=>i1[FIELD_SELECTED_ORDER] - i2[FIELD_SELECTED_ORDER]);
	}
	return filteredItems;
}

export function getOrderedItemsForSummary(items, activeOrder, canEditExistingOrder, sort = true){
	const orderedItems = getOrderedItems(items, sort);
    let filteredItems = [];
    let creditFilteredItems = [];
	let fixedItems = [];
	let creditFixedItems = [];
	let editableItems = [];
	let creditEditableItems = [];
    for(let index in orderedItems) {
        let item = orderedItems[index];
        if(item.isCredit) {
            creditFilteredItems.push(item);
        } else {
            filteredItems.push(item);
        }
    }
	if (canEditExistingOrder) {
		editableItems = filteredItems;
        creditEditableItems = creditFilteredItems;
	} else {
		activeOrder = activeOrder||{};
		filteredItems.forEach(i=>{
			if (activeOrder[i.key]) {
				fixedItems.push(Object.assign({},i,{
				    quantity:activeOrder[i.key].quantity,
                    price:activeOrder[i.key].price
				}));
				if (i.quantity > activeOrder[i.key].quantity) {
					editableItems.push(Object.assign({},i,{
						quantity:i.quantity-activeOrder[i.key].quantity,
                        price:activeOrder[i.key].price
					}));
				}
			} else {
                editableItems.push(i);
			}
		});
        creditFilteredItems.forEach(i=>{
            if (activeOrder[i.key]) {
                creditFixedItems.push(Object.assign({},i,{
                    quantity:activeOrder[i.key].quantity,
                    price:activeOrder[i.key].price
                }));
                if (i.quantity > activeOrder[i.key].quantity) {
                    creditEditableItems.push(Object.assign({},i,{
                        quantity:i.quantity-activeOrder[i.key].quantity,
                        price:activeOrder[i.key].price
                    }));
                }
            } else {
                creditEditableItems.push(i);
            }
        });
	}
	return {
		filteredItems: filteredItems,
		fixedItems: fixedItems,
		editableItems: editableItems,
        creditFilteredItems: creditFilteredItems,
        creditFixedItems: creditFixedItems,
        creditEditableItems: creditEditableItems
	};
}

export function calculateOrderTotal(orderedItems, options, staticData) {

	const emptyPrice = {fullPrice:0, priceBeforeTax:0, tax:0, extraCreditsCost:0};
	options = options || {};

	function calculate() {
		let total = orderedItems.reduce((prev, curr)=> {
			const price = getItemPrice(curr, staticData);
			return {
				fullPrice: prev.fullPrice + price.fullPrice * curr.quantity,
				priceBeforeTax: prev.priceBeforeTax + price.priceBeforeTax * curr.quantity,
				tax: prev.tax + price.tax * curr.quantity,
                isCredit: curr.isCredit,
                extraCreditsCost: prev.extraCreditsCost + (curr.key === options.extraCreditItemKey ? price.fullPrice * curr.quantity : 0)
			};
		}, emptyPrice);
		if (options.extraCost && options.extraCostType === "Fixed" && total.priceBeforeTax) {
			total.extraCostAmount = options.extraCost;
			total.extraCostLabel = (staticData.extraCostLabel || translate("Extra Fuel Charge"));
			total.fullPrice+= options.extraCost;
		}
		if (options.credits && staticData.hasFarmigoCredits) {
			const fullPrice = Math.max(0, total.fullPrice - options.credits);
			total.usedCredits = fullPrice - total.fullPrice;
			total.fullPrice = fullPrice;
			total.remainingCredits = options.credits + total.usedCredits;
            total.creditType = CreditTypes.CREDIT_TYPE_FARMIGO;
		}
		if(options.couponValue) {
            total.couponValue = options.couponValue;
            total.fullPrice -= options.couponValue;
        }
        if(options.creditsTotal && staticData.isCreditItemAsRegularItem) {
            total.creditsTotal = options.creditsTotal > options.credits?options.credits:options.creditsTotal;
            total.priceBeforeTax += options.creditsTotal > options.credits?options.credits:options.creditsTotal;
        }
        if (options.balanceAsCredits >= 0 && options.extraCreditItemKey) {
            total.balanceAsCredits = Math.min(options.balanceAsCredits, total.extraCreditsCost);
            total.remainingBalanceAsCredits = options.balanceAsCredits - total.balanceAsCredits;
            total.fullPrice -= total.balanceAsCredits;
        }
		return total;
	}

	return _formatWrap(
		orderedItems
			? calculate()
			: emptyPrice
	);
}

export function calculateOrderPoints(orderedItems = []) {
    return orderedItems.reduce(function (total, item) {
        return total + (item.points || 0) * item.quantity;
    }, 0)
}

export function getNumOfItemsInOrderText(orderedItems, extraCreditItem) {
    let itemsNum = 0;
    if (orderedItems){
        orderedItems.creditEditableItems.map(item => itemsNum += item.quantity);
        orderedItems.creditFixedItems.map(item => itemsNum += item.quantity);
        orderedItems.editableItems.map(item => {if(item.key !== extraCreditItem.key){itemsNum += item.quantity}});
        orderedItems.fixedItems.map(item => {if(item.key !== extraCreditItem.key){itemsNum += item.quantity}});
    }
    return itemsNum + " " + translate("Item" + (itemsNum === 1 ? "" : "s"));
}

export function getRemainingCredits(creditsData, totals) {
    const orderTotal = creditsData.creditType === CreditTypes.CREDIT_BY_SHARE ? creditsData : (totals.orderTotal || {});
    if(creditsData.credits > 0)
        return {show: true, credits: orderTotal.creditType === CreditTypes.CREDIT_BY_SHARE ? orderTotal.remainingCredits : orderTotal.format().remainingCredits};
    return {show: false};
}

function _roundNumber(number, numOfDecimalDigits) {
    // Default is two digits
    numOfDecimalDigits = numOfDecimalDigits || 2;

    // rounding the number and ensuring no trailing zeros
    const powerOfTen = Math.pow(10, numOfDecimalDigits),
        roundedNumber = Math.round((number ? number : 0) * powerOfTen) / powerOfTen;
    return roundedNumber;
}

function _formatNumber(number, numOfDecimalDigits, keepRemainder) {
	// Default is two digits
	numOfDecimalDigits = numOfDecimalDigits || 2;

	let roundedNumber = _roundNumber(number, numOfDecimalDigits);
	if (keepRemainder) {
        roundedNumber = roundedNumber.toFixed(numOfDecimalDigits);
	}
    // Add , for thousands
	return roundedNumber.toString().replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
}

export function formatCredits(credits) {
    return _formatNumber(credits, 2, false);
}

export function formatMoney (number, numOfDecimalDigits, currency) {
	if (currency == null)
		currency = '$';

	let numberStr = _formatNumber(number, numOfDecimalDigits, true);
	let isNegative = false;

	if (objectUtils.isNumber(number))
		isNegative = number <= -0.01;
	if (objectUtils.isString(number)) {
		number = number.trim();
		isNegative = number.charAt(0) == '-';
	}

	return (isNegative ? '-' : '') + currency + (isNegative ? numberStr.substr(1, numberStr.length) : numberStr);
}

export function formatPoints(points) {
    return _formatNumber(points, 2, true);
}

function _formatWrap(price) {
	return {
		...price,
		format: function(numOfDecimalDigits=2, keepRemainder=true) {
			return {
				fullPrice: _formatNumber(price.fullPrice, numOfDecimalDigits, keepRemainder),
				priceBeforeTax: _formatNumber(price.priceBeforeTax, numOfDecimalDigits, keepRemainder),
				tax: _formatNumber(price.tax, numOfDecimalDigits, keepRemainder),
				extraCostAmount: _formatNumber(price.extraCostAmount, numOfDecimalDigits, keepRemainder),
				usedCredits: _formatNumber(price.usedCredits, numOfDecimalDigits, keepRemainder),
				remainingCredits: _formatNumber(price.remainingCredits, numOfDecimalDigits, keepRemainder)
			}
		},
		formatMoney: function() {
			return {
				fullPrice: formatMoney(price.fullPrice),
				priceBeforeTax: formatMoney(price.priceBeforeTax),
				tax: formatMoney(price.tax),
				extraCostAmount: formatMoney(price.extraCostAmount),
				usedCredits: formatMoney(price.usedCredits),
				remainingCredits: formatMoney(price.remainingCredits)
			}
		}
	}
}

export function formatPrice(price){
    return {
        fullPrice: formatMoney(price.fullPrice),
        priceBeforeTax: formatMoney(price.priceBeforeTax),
        tax: formatMoney(price.tax),
        extraCostAmount: formatMoney(price.extraCostAmount),
        usedCredits: formatMoney(price.usedCredits),
        remainingCredits: formatMoney(price.remainingCredits),
        couponValue: "-"+formatMoney(price.couponValue),
        balanceAsCredits: "-"+formatMoney(price.balanceAsCredits)
    }
}

export function getCheckoutPrice(totals){
    if (totals && totals.editableTotal)
        return formatPrice(totals.editableTotal).fullPrice;
     return formatPrice(0).fullPrice;
}

export function getItemKeysLists(items) {
    let listItems = [];
    let waitListItems = [];
    const selectedItems = getJoinSelectedItems(items);
    for(let index in selectedItems.waitlistItems) {
        const item = selectedItems.waitlistItems[index];
        waitListItems.push(item.key);

    }
    for(let index in selectedItems.items) {
        const item = selectedItems.items[index];
        listItems.push(item.key);
    }
    return {
        waitListItems: waitListItems,
        listItems: listItems
    }
}

export function getWaitlistItems(items) {
    let waitListItems = [];
    const selectedItems = getJoinSelectedItems(items);
    for(let index in selectedItems.waitlistItems) {
        const item = selectedItems.waitlistItems[index];
        for(let i=0;i<item.quantity;i++) {
            waitListItems.push(item.key);
        }
    }
    return waitListItems;
}

export function getItemPrice(item, serverStaticData, pickupsite) {
	let price = item.price || 0;
    if(pickupsite && item.pickupSitePriceMap && item.pickupSitePriceMap.hasOwnProperty(pickupsite)){
        price =  item.pickupSitePriceMap[pickupsite];
    }
	const itemPrice = serverStaticData.isSupportSalesTax ? _roundNumber(price * 100.00 / (100.00 + (item.salesTax || 0))) : price;
	return _formatWrap({
		fullPrice: price,
		priceBeforeTax: itemPrice,
		tax: serverStaticData.isSupportSalesTax ? price - itemPrice : undefined
	});
}

export const orderValidator = {
	validateMinimumPerOrder: function(items, minimumPerOrder, extraCost, extraCostType, credits, staticData, shareItems) {
		const orderedItems = getOrderedItems(items, false);
        let total = calculateOrderTotal(orderedItems, {extraCost, extraCostType, credits}, staticData);
        minimumPerOrder = minimumPerOrder || 0;
        if (staticData.showItemPoints) {
            const totalPoints = calculateOrderPoints(orderedItems);
            if (totalPoints < minimumPerOrder) {
                return "In order to checkout you need to reach a total of at least " + minimumPerOrder + " points. " +
                    "Please add " + formatPoints(minimumPerOrder - totalPoints) + " points to checkout.";
            }
            return;
        }
        let price = (total && (total.priceBeforeTax || 0) + (total.tax || 0))||0;
        const skipCheckMinimum = staticData.checkMinimumOrderOnlyForStoreAccess && shareItems.some(share => share.price > 0)
        if (!skipCheckMinimum && price < minimumPerOrder) {
            const formattedMin = formatMoney(minimumPerOrder);
            if (credits) {
                return translate("In order to checkout your sub total order needs to exceed ") + formattedMin + ".";
            } else if (extraCost && extraCostType === "Fixed" && total.extraCostLabel) {
                return translate("In order to checkout, your order needs to exceed ") + formattedMin
                    + translate(" (not including ") + total.extraCostLabel.toLowerCase() + ").";
            } else {
                return translate("_minimumPerOrder") + formattedMin + ".";
            }
        }
	},
    validateCouponMinimum: function (items, extraCost, extraCostType, credits, staticData, activeOrder, coupon) {
        if (coupon) {
            const orderedItems = getOrderedItemsForSummary(items, activeOrder, false);
            let total = calculateOrderTotal(orderedItems.filteredItems, {extraCost, extraCostType, credits}, staticData);
            const minimumOrder = coupon.minimumOrder || 0;
            let price = (total && (total.priceBeforeTax || 0) + (total.tax || 0)) || 0;
            if (coupon.useMinimumOrder && price < minimumOrder) {
                const formattedMin = formatMoney(minimumOrder);
                return translate("Order more than ") + formattedMin + translate(" to redeem the coupon.");
            }
            else if(coupon.discount >= price) {
                const formattedDiscount = formatMoney(coupon.discount);
                return translate("Order more than ") + formattedDiscount + translate(" to redeem the coupon.");
            }
        }
    }
};

export function calcTotals(isSeasonal, pickupsite, items, paymentPlan, coupon, itemsRemainingDates, remainingSchedulesDates, nextDeliveryDate, isAddSubscription, subscriptionDiscount, staticData, paymentMethod){

    let totals = [];
    let subTotal = 0;
    let total = 0;
    let deliveriesNum = 1;
    let extraCost = 0;
    let signupFee =  isAddSubscription ? 0 : pickupsite.signupFee;
    let tax = 0;
    let paymentPlanDiscountAmount = 0;

    //Don't show totals if there are no items
    if (items.length == 0)
        return {};
    //Calc subtotal based on the payment plan
    switch (paymentPlan.type) {
        case (PaymentPlans.P):
            //For prepay the plan amount is the total
            subTotal = paymentPlan.amount;
            break;
        default:
            //Calc total of all items for the whole season
            // for(let key in items){
            //     let item = items[key];
            items.map(function (item) {
                const itemDeliveriesNum = calcNumberOfDeliveries(isSeasonal, item, paymentPlan, item.selectedSchedule, itemsRemainingDates, remainingSchedulesDates[item.selectedSchedule], nextDeliveryDate);
                //item.price already includes the tax
                const price = item.pickupSitePriceMap && item.pickupSitePriceMap.hasOwnProperty(pickupsite.key) ? item.pickupSitePriceMap[pickupsite.key] : item.price;
                const itemPriceBeforeTax = !item.salesTax ? price : (price / (1 + item.salesTax / 100));
                const itemSubTotal = itemDeliveriesNum * itemPriceBeforeTax * item.quantity;
                const itemTax = itemDeliveriesNum * (price - itemPriceBeforeTax) * item.quantity;
                subTotal += itemSubTotal;
                tax += itemTax;
                //Calc the payment plan discount only if not add-subscription and if not an option
                if (!isAddSubscription && !(paymentPlan.type ===  PaymentPlans.DistributionDynamicPrePayment && item.isAdditional)) {
                    paymentPlanDiscountAmount += itemSubTotal * (paymentPlan.discount / 100);
                    tax -= itemTax * (paymentPlan.discount / 100);
                }
                deliveriesNum = Math.max(deliveriesNum, itemDeliveriesNum);
            });
    }

    //Calc extra cost
    if (pickupsite.extraCost > 0) {
        if (pickupsite.extraCostType === 'Fixed') {
            //In case of non pro-rate need to calc the actual remaining deliveries for the extra cost
            let actualDeliveriesNum = 0;
            let remainingAllDates = new Set();
            items.forEach(item => remainingSchedulesDates[item.selectedSchedule].forEach(date => remainingAllDates.add(date)));
            actualDeliveriesNum = calcActualRemainingDeliveries(Array.from(remainingAllDates).sort(), itemsRemainingDates, nextDeliveryDate, paymentPlan.amount);

            // The max amount is being considered when calling calcActualRemainingDeliveries()
            // if (paymentPlan.type === PaymentPlans.DistributionDynamicPrePayment) {
            //     actualDeliveriesNum = deliveriesNum;
            // }
            if (!isAddSubscription) // In add subscription the fixed cost should be already calculated
                extraCost = pickupsite.extraCost * actualDeliveriesNum;
        } else {
            //Apply payment plan discount before calculating extra cost
            extraCost = (subTotal - paymentPlanDiscountAmount) * pickupsite.extraCost / 100;
            //Add extra cost also to the signup fee
            signupFee *= (1 + (pickupsite.extraCost / 100));
        }
    }

    //Add the tax & extra cost only if not PaymentPlans.P
    total = subTotal + signupFee + ((paymentPlan.type !== PaymentPlans.P) ? (tax + extraCost) : 0);

    //Add subtotal row only for specific payment plans
    const isFullSeasonPaymentPlan = (paymentPlan.type === PaymentPlans.F || paymentPlan.type === PaymentPlans.AutoRecurring || paymentPlan.type === PaymentPlans.PresetRecurring);
    const applyPercentageCoupon = isFullSeasonPaymentPlan || paymentPlan.type === PaymentPlans.DistributionDynamicPrePayment || paymentPlan.type === PaymentPlans.DynamicPrePayment;

    //First apply payment plan discount (before coupon)
    total -= paymentPlanDiscountAmount;

    //Calc coupon
    let couponDiscount = 0;
    if (coupon && coupon.valid)
        if (coupon.isFixedAmount)
            couponDiscount = coupon.discount;
        else
            couponDiscount = total * (coupon.discount / 100);
    //Coupon should impact the total only if fixed or for seasonal payment plan
    if (couponDiscount && (coupon.isFixedAmount || applyPercentageCoupon))
        total -= couponDiscount;


    //Calc the subscription discount only if  add-subscription
    let subscriptionDiscountAmount = isAddSubscription && subscriptionDiscount ? total * (subscriptionDiscount / 100) : 0;
    total -= subscriptionDiscountAmount;

    //Show subtotal only for seasonal
    if (isFullSeasonPaymentPlan)
        totals.push({text:"Subtotal", amount:formatMoney(subTotal)});

    //Add Tax
    if (tax > 0)
        totals.push({text:"Tax", amount:formatMoney(tax)});

    //Add signup fee row
    if (signupFee > 0)
        totals.push({text:(staticData || {}).membershipFeeLabel || "Membership Fee", amount:formatMoney(signupFee)});

    //Add extra cost row
    if (extraCost > 0) {
        const extraCostLabel = (staticData || {}).extraCostLabel || "Extra Fuel Charge";
        const extraCostText = pickupsite.extraCostType === 'Fixed' ? ('$' + pickupsite.extraCost + " per delivery") : (pickupsite.extraCost + '%');
        const extraCostAmount = isFullSeasonPaymentPlan ? formatMoney(extraCost) : '';
        totals.push({text: extraCostLabel + " (" + extraCostText + ")", amount: extraCostAmount});
    }

    //Add Coupon row
    if (couponDiscount > 0) {
        const couponText = 'Coupon (' + (coupon.isFixedAmount ? ('$' + coupon.discount) : (coupon.discount + '%')) + ' discount)';
        const couponAmount = (coupon.isFixedAmount || applyPercentageCoupon) ? formatMoney(-1 * couponDiscount) : '';
        totals.push({text: couponText, amount: couponAmount});
    }

    //Add payment plan discount row
    if (paymentPlanDiscountAmount > 0)
        totals.push({
            text: "Special Discount (" + paymentPlan.discount + "%)",
            amount: formatMoney(-1 * paymentPlanDiscountAmount)
        });

    //Add subscription discount row
    if (subscriptionDiscountAmount > 0)
        totals.push({
            text: "Special Discount (" + subscriptionDiscount + "%)",
            amount: formatMoney(-1 * subscriptionDiscountAmount)
        });

    if (staticData.applyCreditCardFee && staticData.creditCardFee > 0 &&
        (PaymentMethod.AUTHORIZE_DOT_NET.key === paymentMethod || PaymentMethod.AUTHORIZE_DOT_NET_PREAPPROVE.key === paymentMethod)) {
        let creditCardFee = total * (staticData.creditCardFee / 100);
        total += creditCardFee;
        totals.push({text: staticData.creditCardFeeLabel, amount: formatMoney(creditCardFee)});
    }
    //Format the text of the grand total label
    const totalLabel = paymentPlan.type === PaymentPlans.AutoRecurring || paymentPlan.type === PaymentPlans.PresetRecurring ?
        "Total" : "Total due at checkout";

    //Format the price text
    let priceText = "";
    switch (paymentPlan.type) {
        case PaymentPlans.PresetRecurring:
        case PaymentPlans.AutoRecurring:
            //In case of installments the signup fee is added to the first installment (after discount)
            signupFee *= (1 - paymentPlan.discount / 100);
            let installmentPrice = (total - signupFee) / paymentPlan.installments;
            priceText = signupFee == 0 ?
                paymentPlan.installments + " * " + formatMoney(installmentPrice) :
            formatMoney(installmentPrice + signupFee) + " + " + (paymentPlan.installments - 1) + " * " + formatMoney(installmentPrice);
            break;
        case PaymentPlans.OneWeekDebit:
            //Only membership fee should be charged
            priceText = formatMoney(signupFee);
            break;
        default:
            priceText = formatMoney(total);
    }

    totals.push({text:translate(totalLabel), amount:priceText, type: "TOTAL_DUE"});

    return {priceText, totals};
}

export function getAllItemsRemainingDeliveryDates(items, remainingScheduleDates){

    //Get all dates of relevant schedules
    let deliveryDatesSet = new Set();
    let schedulesSet = new Set();
    items.map(item => {
        const schedule = item.selectedSchedule ? item.selectedSchedule : item.schedules[0];
        if (!schedulesSet.has(schedule)) {
            schedulesSet.add(schedule);
            for (let date of remainingScheduleDates[schedule]) {
                deliveryDatesSet.add(date);
            }
        }
    });

    //Sort
    let deliveryDates = Array.from(deliveryDatesSet.values()).sort((d1, d2) => {
        return d1.localeCompare(d2);
    });

    return deliveryDates;
}

function calcNumberOfDeliveries(isSeasonal, item, paymentPlan, schedule, itemsRemainingDates, remainingScheduleDates, nextDeliveryDate){

    if (paymentPlan.type === PaymentPlans.DynamicPrePayment){
        //paymentPlan.amount is the number of deliveries
        return paymentPlan.amount;
    }
    if (paymentPlan.type === PaymentPlans.DistributionDynamicPrePayment){
        if (paymentPlan.amount)
            return calcActualRemainingDeliveries(remainingScheduleDates, itemsRemainingDates, nextDeliveryDate, paymentPlan.amount);
        //if paymentPlan.amount == 0 then get the date of end of the month of the first delivery
        const endOfMonth = itemsRemainingDates[0].slice(0, 8) + "31";
        let deliveries = 0;
        //Calc how many dates are until the end of the months
        for (let date of remainingScheduleDates)
            if (date.localeCompare(endOfMonth) <= 0)
                deliveries++;
            else
                break;
        return deliveries;
    }
    if (isSeasonal){
        let scheduleIndex = -1;
        if(!item || !item.schedules) {
            const error = {
                item: item,
                schedules: item.schedules
            };
            Raven.captureMessage("Schedules is undefined in basket - line 522", {extra: error});
        } else {
            scheduleIndex = item.schedules.findIndex(scheduleElem => {
                return scheduleElem == schedule;
            });
        }
        return item.weeksLeft[(scheduleIndex < 0) ? 0 : scheduleIndex];
    }
    //If ongoing
    return 1;
}

function calcActualRemainingDeliveries(scheduleDates, remainingScheduleDates, nextDeliveryDate, maxDeliveries){
    //Calc number of possible deliveries
    let deliveries = 0;
    let deliveryDates = remainingScheduleDates;
    //Find the first date to start from
    let firstIndex = remainingScheduleDates.indexOf(nextDeliveryDate);
    if (firstIndex < 0)
        firstIndex = 0;
    if (maxDeliveries && maxDeliveries > 0)
        deliveryDates = remainingScheduleDates.slice(firstIndex, firstIndex + maxDeliveries);
    //Calc how many dates are in the item schedule
    for (let date of scheduleDates)
        if (deliveryDates.includes(date))
            deliveries++;
        else
            break;
    return deliveries;
}

export function getJoinSelectedItems(shares, options){
    let items = [];
    let waitlistItems = [];
    let allItems  = Object.assign({}, shares, options);
    Object.keys(allItems).map(key => {
        const item = allItems[key];
        if(item.quantity > 0)
            if (item.waitList)
                waitlistItems.push(item);
            else
                items.push(item);
    });
    return {items, waitlistItems};
}

export function getDistributionsNumber(item, selectedSchedule){
	const scheduleIndex = selectedSchedule ? item.schedules.indexOf(selectedSchedule) : 0;
	return item.weeksLeft ? item.weeksLeft[scheduleIndex] : 0;
}

export function getNextDeliveryDate(pickupSite, items, schedules, nextDeliveryDates) {
    if (!pickupSite || !pickupSite.deliveryDaysList)
        return null;
    const pickupSiteDeliveryDay = pickupSite.deliveryDaysList[0];
    const scheduleToDate = nextDeliveryDates[pickupSiteDeliveryDay];
    if (items)
        schedules = getSelectedSchedules(items, schedules);
    if (!scheduleToDate || schedules.length === 0) {
        return null;
    }
    let date = MAX_DATE_IN_SERVER_FORMAT;
    for (let schedule in schedules) {
        if (schedules.hasOwnProperty(schedule)){
            let prospectDate = scheduleToDate[schedule];
            if (prospectDate !== null && date > prospectDate) {
                date = prospectDate;
            }
        }
    }
    return date === MAX_DATE_IN_SERVER_FORMAT ? scheduleToDate[Object.keys(scheduleToDate)[0]] : date;
}


function getSelectedSchedules(items, schedules){
    const selectedItems = items || {};
    let selectedSchedules = {};
    for(let i=0; i < selectedItems.length; i++){
        const item = selectedItems[i];
        if(item.selectedSchedule){
            selectedSchedules[item.selectedSchedule] = schedules[item.selectedSchedule];
        }
    }
    return Object.keys(selectedSchedules).length > 0 ?  selectedSchedules : schedules;
}

export function isEBTNonEligibleItems(items) {
    const orderedItems = getOrderedItems(items);
    return orderedItems.filter(item => (item.label || '').toLowerCase().includes('snap non eligible')).length > 0;
}



// WEBPACK FOOTER //
// ./src/common/basket.js